// js/lib/auth.js
// Thin wrapper around Firebase Authentication.
// All auth operations go through here — never call Firebase auth directly from pages.

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
} from "firebase/auth";

import { auth } from "./firebase.js";

export function signIn(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

export function register(email, password) {
  return createUserWithEmailAndPassword(auth, email, password);
}

export function signOut() {
  return firebaseSignOut(auth);
}

export function resetPassword(email) {
  return sendPasswordResetEmail(auth, email);
}

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

/**
 * Sign in (or register) with Google popup.
 * Returns { user, isNewUser }
 */
export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  // isNewUser is available via getAdditionalUserInfo but we check Firestore instead
  return result.user;
}

/**
 * Subscribe to auth state changes.
 * @param {(user: import("firebase/auth").User | null) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

export function getCurrentUser() {
  return auth.currentUser;
}

/**
 * Send a verification email to the given user.
 *
 * Firebase Auth only supports a single custom template per email type, so we
 * always use the English template (language-neutral for a global SaaS product).
 * Multi-language transactional emails are tracked as a future task — see
 * docs/ai-tasks.md (Task: Multi-language verification emails via Cloud Functions).
 *
 * @param {import("firebase/auth").User} user
 */
export function sendVerificationEmail(user) {
  auth.languageCode = "en";
  return sendEmailVerification(user);
}

/**
 * Sign in using a Firebase custom token (used for admin impersonation).
 * @param {string} token
 */
export function signInWithToken(token) {
  return signInWithCustomToken(auth, token);
}
